import { customFieldRecordKey } from '@/features/custom-fields';
import {
  expiringBatchesResponseSchema,
  productBatchListResponseSchema,
  productBatchSchema,
  productBulkPriceUpdateResultSchema,
  productDetailSchema,
  productListResponseSchema,
  productBaseUnitsSchema,
  productPriceResponseSchema,
  stockAdjustmentResultSchema,
  stockMovementListResponseSchema,
  type ExpiringBatchesResponse,
  type CustomFieldFilterValues,
  type ProductBatch,
  type ProductBatchCreateInput,
  type ProductBatchListResponse,
  type ProductBatchUpdateInput,
  type ProductBulkPriceUpdateInput,
  type ProductBulkPriceUpdateResult,
  type ProductCreateInput,
  type ProductDetail,
  type ProductListResponse,
  type ProductPriceResponse,
  type ProductUpdateInput,
  type StockAdjustmentInput,
  type StockAdjustmentResult,
  type StockMovementListResponse,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiDownload, apiFetch, apiSend, saveBlob, type ApiError } from '@/lib/api-client';

export interface ProductListParams {
  page: number;
  pageSize: number;
  sort: string;
  q?: string | undefined;
  status?: string[];
  type?: string[];
  productGroup?: string[];
  tags?: string[];
  isBatchManaged?: boolean | undefined;
  belowMinStock?: boolean | undefined;
  expiringWithinDays?: number | undefined;
  activeOnly?: boolean | undefined;
  customFieldFilters?: CustomFieldFilterValues | undefined;
}

export function buildProductQuery(params: ProductListParams): string {
  const search = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    sort: params.sort,
  });

  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }
  if (params.status !== undefined && params.status.length > 0) {
    search.set('status', params.status.join(','));
  }
  if (params.type !== undefined && params.type.length > 0) {
    search.set('type', params.type.join(','));
  }
  if (params.productGroup !== undefined && params.productGroup.length > 0) {
    search.set('productGroup', params.productGroup.join(','));
  }
  if (params.tags !== undefined && params.tags.length > 0) {
    search.set('tags', params.tags.join(','));
  }
  if (params.isBatchManaged !== undefined) {
    search.set('isBatchManaged', String(params.isBatchManaged));
  }
  if (params.belowMinStock === true) {
    search.set('belowMinStock', 'true');
  }
  if (params.expiringWithinDays !== undefined) {
    search.set('expiringWithinDays', String(params.expiringWithinDays));
  }
  if (params.activeOnly === true) {
    search.set('activeOnly', 'true');
  }
  if (
    params.customFieldFilters !== undefined &&
    Object.keys(params.customFieldFilters).length > 0
  ) {
    search.set('customFieldFilters', JSON.stringify(params.customFieldFilters));
  }

  return search.toString();
}

export interface MovementListParams {
  page: number;
  pageSize: number;
  sort: string;
  type?: string[];
  batchId?: string | undefined;
}

function buildMovementQuery(params: MovementListParams): string {
  const search = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    sort: params.sort,
  });

  if (params.type !== undefined && params.type.length > 0) {
    search.set('type', params.type.join(','));
  }
  if (params.batchId !== undefined) {
    search.set('batchId', params.batchId);
  }

  return search.toString();
}

export const productKeys = {
  all: ['products'] as const,
  list: (params: ProductListParams) => ['products', 'list', params] as const,
  detail: (id: string) => ['products', 'detail', id] as const,
  batches: (id: string) => ['products', 'detail', id, 'batches'] as const,
  batch: (id: string) => ['inventory', 'batches', id] as const,
  movements: (id: string, params: MovementListParams) =>
    ['products', 'detail', id, 'movements', params] as const,
  price: (id: string, quantity: string, priceGroup: string) =>
    ['products', 'detail', id, 'price', quantity, priceGroup] as const,
  expiring: (withinDays: number) => ['inventory', 'expiring-batches', withinDays] as const,
};

export function useProductList(
  params: ProductListParams,
  enabled = true,
): UseQueryResult<ProductListResponse, ApiError> {
  return useQuery({
    enabled,
    queryKey: productKeys.list(params),
    queryFn: () =>
      apiFetch(`/api/v1/products?${buildProductQuery(params)}`, productListResponseSchema),
    placeholderData: (previous) => previous,
  });
}

export function useProduct(id: string): UseQueryResult<ProductDetail, ApiError> {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => apiFetch(`/api/v1/products/${id}`, productDetailSchema),
  });
}

export function useCreateProduct(): UseMutationResult<ProductDetail, ApiError, ProductCreateInput> {
  const queryClient = useQueryClient();

  return useMutation<ProductDetail, ApiError, ProductCreateInput>({
    mutationFn: (input) =>
      apiFetch('/api/v1/products', productDetailSchema, { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useUpdateProduct(
  id: string,
): UseMutationResult<ProductDetail, ApiError, ProductUpdateInput> {
  const queryClient = useQueryClient();

  return useMutation<ProductDetail, ApiError, ProductUpdateInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/products/${id}`, productDetailSchema, { method: 'PATCH', body: input }),
    onSuccess: (product) => {
      void queryClient.invalidateQueries({ queryKey: customFieldRecordKey('PRODUCT', id) });
      queryClient.setQueryData(productKeys.detail(id), product);
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteProduct(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiSend(`/api/v1/products/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useProductExport(): UseMutationResult<
  void,
  ApiError,
  { params: ProductListParams; format: 'csv' | 'xlsx' }
> {
  return useMutation<void, ApiError, { params: ProductListParams; format: 'csv' | 'xlsx' }>({
    mutationFn: async ({ params, format }) => {
      const query = buildProductQuery({ ...params, page: 1, pageSize: 200 });
      const result = await apiDownload(
        `/api/v1/products/export?${query}&format=${format}`,
        `artikel.${format}`,
      );
      saveBlob(result);
    },
  });
}

export function useBulkPriceUpdate(): UseMutationResult<
  ProductBulkPriceUpdateResult,
  ApiError,
  ProductBulkPriceUpdateInput
> {
  const queryClient = useQueryClient();

  return useMutation<ProductBulkPriceUpdateResult, ApiError, ProductBulkPriceUpdateInput>({
    mutationFn: (input) =>
      apiFetch('/api/v1/products/bulk-price-update', productBulkPriceUpdateResultSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useProductPrice(
  id: string,
  quantity: string,
  priceGroup: string,
): UseQueryResult<ProductPriceResponse, ApiError> {
  return useQuery({
    queryKey: productKeys.price(id, quantity, priceGroup),
    queryFn: () => {
      const search = new URLSearchParams({ quantity });
      if (priceGroup.length > 0) {
        search.set('priceGroup', priceGroup);
      }
      return apiFetch(
        `/api/v1/products/${id}/price?${search.toString()}`,
        productPriceResponseSchema,
      );
    },
    enabled: Number(quantity) > 0,
  });
}

// ---------------------------------------------------------------------------
// Chargen und Bestand
// ---------------------------------------------------------------------------

export function useProductBatches(
  id: string,
  includeDepleted: boolean,
  filters: Record<string, string> = {},
  enabled = true,
): UseQueryResult<ProductBatchListResponse, ApiError> {
  return useQuery({
    enabled,
    queryKey: [...productKeys.batches(id), includeDepleted, filters],
    queryFn: () =>
      apiFetch(
        `/api/v1/products/${id}/batches?${new URLSearchParams({ ...filters, includeDepleted: String(includeDepleted) }).toString()}`,
        productBatchListResponseSchema,
      ),
  });
}

export function useBatch(id: string): UseQueryResult<ProductBatch, ApiError> {
  return useQuery({
    queryKey: productKeys.batch(id),
    queryFn: () => apiFetch(`/api/v1/inventory/batches/${id}`, productBatchSchema),
  });
}

export function useCreateBatch(
  productId: string,
): UseMutationResult<ProductBatch, ApiError, ProductBatchCreateInput> {
  const queryClient = useQueryClient();

  return useMutation<ProductBatch, ApiError, ProductBatchCreateInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/products/${productId}/batches`, productBatchSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useUpdateBatch(
  productId: string,
): UseMutationResult<ProductBatch, ApiError, { batchId: string; input: ProductBatchUpdateInput }> {
  const queryClient = useQueryClient();

  return useMutation<ProductBatch, ApiError, { batchId: string; input: ProductBatchUpdateInput }>({
    mutationFn: ({ batchId, input }) =>
      apiFetch(`/api/v1/products/${productId}/batches/${batchId}`, productBatchSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useStockAdjustment(
  productId: string,
): UseMutationResult<StockAdjustmentResult, ApiError, StockAdjustmentInput> {
  const queryClient = useQueryClient();

  return useMutation<StockAdjustmentResult, ApiError, StockAdjustmentInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/products/${productId}/stock-adjustment`, stockAdjustmentResultSchema, {
        method: 'POST',
        headers: { 'Idempotency-Key': input.idempotencyKey },
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useStockMovements(
  productId: string,
  params: MovementListParams,
): UseQueryResult<StockMovementListResponse, ApiError> {
  return useQuery({
    queryKey: productKeys.movements(productId, params),
    queryFn: () =>
      apiFetch(
        `/api/v1/products/${productId}/movements?${buildMovementQuery(params)}`,
        stockMovementListResponseSchema,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useExpiringBatches(
  withinDays: number,
): UseQueryResult<ExpiringBatchesResponse, ApiError> {
  return useQuery({
    queryKey: productKeys.expiring(withinDays),
    queryFn: () =>
      apiFetch(
        `/api/v1/inventory/expiring-batches?withinDays=${String(withinDays)}&limit=10`,
        expiringBatchesResponseSchema,
      ),
  });
}

export function useProductBaseUnits(enabled = true) {
  return useQuery({
    queryKey: ['products', 'base-units'],
    queryFn: () => apiFetch('/api/v1/products/base-units', productBaseUnitsSchema),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
